import { useState } from 'react';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { Eye, EyeOff } from 'lucide-react';
import { useChangePassword, accountErrorMessage } from '@/api/account';
import styles from './Information.module.css';

const MIN_LENGTH = 8;

/** Change-password form. Routes SSO vs local accounts inside `useChangePassword`. */
export function ChangePasswordCard() {
  const { show } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const change = useChangePassword({
    onSuccess: () => {
      show({ tone: 'success', title: 'Password updated' });
      setCurrent('');
      setNext('');
      setConfirm('');
      setError(null);
    },
    onError: (e) =>
      show({
        tone: 'danger',
        title: 'Could not change password',
        description: accountErrorMessage(e, 'Please check your current password and try again.'),
      }),
  });

  function validate(): string | null {
    if (!current) return 'Enter your current password.';
    if (next.length < MIN_LENGTH) return `New password must be at least ${MIN_LENGTH} characters.`;
    if (next === current) return 'New password must differ from the current one.';
    if (next !== confirm) return 'New password and confirmation do not match.';
    return null;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    change.mutate({
      current_password: current,
      new_password: next,
      new_password_confirmation: confirm,
    });
  }

  const revealToggle = (
    <button
      type="button"
      onClick={() => setReveal((r) => !r)}
      aria-label={reveal ? 'Hide passwords' : 'Show passwords'}
      style={{
        display: 'inline-flex',
        border: 'none',
        background: 'transparent',
        color: 'var(--vn-fg-muted)',
        cursor: 'pointer',
        padding: 0,
      }}
    >
      {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
    </button>
  );

  const type = reveal ? 'text' : 'password';
  const dirty = !!(current || next || confirm);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change password</CardTitle>
        <CardDescription>
          Enter your current password, then choose a new one. Use at least {MIN_LENGTH} characters.
        </CardDescription>
      </CardHeader>
      <CardBody>
        <form id="change-password-form" onSubmit={handleSubmit}>
          <div className={styles.form}>
            <Field label="Current password" required>
                {(props) => (
                  <Input
                    {...props}
                    type={type}
                    autoComplete="current-password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    endSlot={revealToggle}
                  />
                )}
              </Field>
              <Field label="New password" required>
                {(props) => (
                  <Input
                    {...props}
                    type={type}
                    autoComplete="new-password"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Confirm new password" required error={error ?? undefined}>
                {(props) => (
                  <Input
                    {...props}
                    type={type}
                    autoComplete="new-password"
                    value={confirm}
                    invalid={!!error}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                )}
              </Field>
          </div>
        </form>
      </CardBody>
      <CardFooter>
        <Button
          type="submit"
          form="change-password-form"
          size="sm"
          disabled={!dirty || change.isPending}
        >
          {change.isPending ? 'Updating…' : 'Update password'}
        </Button>
      </CardFooter>
    </Card>
  );
}
